import PropTypes from 'prop-types';
import React from 'react';

// Ensure React is globally available for third-party components (Tagify)
window.React = React;

import Popup from "reactjs-popup";
import Iframe from 'react-iframe'
import Tags from "@yaireo/tagify/dist/react.tagify";

import useIsMobile from "../../shared/hooks/useIsMobile";
import MailIcon from "../../shared/Icons/MailIcon";
import DownloadIcon from "../../shared/Icons/DownloadIcon";
import DocumentIcon from "../../shared/Icons/DocumentIcon";
import ChevronDownIcon from "../../shared/Icons/ChevronDownIcon";
import EyeIcon from "../../shared/Icons/EyeIcon";
import PlusIcon from "../../shared/Icons/PlusIcon";
import SectionHeading from "./SectionHeading";
import FieldLabel from "./FieldLabel";
import NotesPicker from "./NotesPicker";
import DaySheetSectionCard from "./DaySheetSectionCard";

import "../styles/setlistPopups.css";

const modalStyle = {
  border: "none",
  borderRadius: "16px",
  boxShadow: "var(--sl-shadow-lg)",
  width: "min(1120px, calc(100vw - 32px))",
  height: "min(768px, calc(100vh - 48px))",
  padding: "0",
  background: "var(--sl-bg-primary)",
  overflow: "hidden",
};

const LAYOUTS = [
  { value: "", label: "Standard advance", hint: "The classic advance — full details, current format." },
  { value: "backstage", label: "Backstage day sheet", hint: "One page, big type — built to tape to the wall." },
  { value: "foh", label: "Front of house day sheet", hint: "Public-safe: no internal notes or contacts." },
];

const SECTIONS = [
  { id: "lineup", label: "Lineup", desc: "Acts and set times" },
  { id: "schedule", label: "Schedule", desc: "Doors through curfew" },
  { id: "start", label: "Start times", desc: "When each activity begins" },
  { id: "length", label: "Set length", desc: "Minutes between schedule times" },
  { id: "end_time", label: "End times", desc: "When each activity wraps" },
  { id: "notes", label: "Notes", desc: "Event notes you pick" },
  { id: "contacts", label: "Contacts", desc: "Production day-of numbers" },
  { id: "age", label: "Age policy", desc: "Door restrictions" },
];
const FIELDS = SECTIONS.map((s) => s.id);

const FONT_SIZES = [
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
];

const PRESET_FIELDS = {
  backstage: { lineup: true, schedule: true, start: true, length: false, end_time: false, notes: true, contacts: true, age: true, font_size: "medium" },
  foh: { lineup: true, schedule: true, start: true, length: false, end_time: false, notes: false, contacts: false, age: true, font_size: "large" },
};

const FORCE_OFF = { foh: ["notes", "contacts"] };

const buildFields = (preset) => Object.fromEntries(FIELDS.map((f) => [f, !!preset[f]]));

const canSubmitForm = (showAdvanceSend) =>
  !!(showAdvanceSend.to && showAdvanceSend.to.length > 0 && showAdvanceSend.subject && showAdvanceSend.subject.length > 0);

const recipientCount = (showAdvanceSend) =>
  (showAdvanceSend.to || "").split(",").map((s) => s.trim()).filter(Boolean).length;

const showAdvanceUrl = (team, confirm, showAdvanceSend, daySheet, format) => {
  const baseUrl = "/teams/" + team.id + "/confirms/" + confirm.id + "/print_show_details" + (format || "");
  const params = [];

  (showAdvanceSend.note_ids || []).forEach((noteId) => params.push("note_ids[]=" + noteId));

  if (daySheet && daySheet.template) {
    params.push("layout=day_sheet");
    params.push("template=" + daySheet.template);
    params.push("font_size=" + daySheet.font_size);
    FIELDS.forEach((f) => params.push(f + "=" + (daySheet.fields[f] ? "1" : "0")));
  }

  return params.length ? baseUrl + "?" + params.join("&") : baseUrl;
};

const ShowAdvanceSendPopup = ({
  csrfToken,
  team,
  confirm,
  currentUser,
  showAdvanceSend,
  showAdvanceSendChanged,
  showAdvanceSendToChanged,
  showAdvanceSendSubjectChanged,
  showAdvanceSendBodyChanged,
  createShowAdvanceSend,
  calendarEventContacts,
  notes,
  showAdvanceSendNoteClicked,
  toggleHideLineupFromAdvance,
  toggleHideShowDetailsFromAdvance,
  initialMode
}) => {
  const canSend = !!currentUser.can_create_show_advance_send;
  const isMobile = useIsMobile();
  const tagifyRef = React.useRef();
  const [mode, setMode] = React.useState(canSend && initialMode === "email" ? "email" : "pdf");
  const [daySheet, setDaySheet] = React.useState({
    template: "backstage",
    font_size: PRESET_FIELDS.backstage.font_size,
    fields: buildFields(PRESET_FIELDS.backstage),
  });

  React.useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'toggleLineup') {
        toggleHideLineupFromAdvance(csrfToken, confirm);
      } else if (event.data && event.data.type === 'toggleShowDetails') {
        toggleHideShowDetailsFromAdvance(csrfToken, confirm);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [toggleHideLineupFromAdvance, toggleHideShowDetailsFromAdvance, csrfToken, confirm]);

  const isEmail = mode === "email";
  const isPdf = mode === "pdf";
  const isDaySheet = !!daySheet.template;

  const selectLayout = (template) => {
    if (!template) { setDaySheet((d) => ({ ...d, template: "" })); return; }
    const preset = PRESET_FIELDS[template];
    setDaySheet({ template, font_size: preset.font_size, fields: buildFields(preset) });
  };

  const toggleField = (id) => setDaySheet((d) => ({ ...d, fields: { ...d.fields, [id]: !d.fields[id] } }));

  const layoutHint = (LAYOUTS.find((l) => l.value === daySheet.template) || LAYOUTS[0]).hint;
  const selectedNoteIds = showAdvanceSend.note_ids || [];

  const eventContacts = calendarEventContacts || [];
  const selectedEmails = new Set((showAdvanceSend.to || "").split(",").map((e) => e.trim()).filter(Boolean));
  const contactSuggestions = [];
  const seenEmails = new Set();
  eventContacts.forEach((cec) => {
    const contact = cec.contact;
    const email = contact && contact.email;
    if (!email || selectedEmails.has(email) || seenEmails.has(email)) return;
    seenEmails.add(email);
    const role = contact.contact_type ? contact.contact_type.split(" (")[0] : null;
    const name = contact.name || email;
    contactSuggestions.push({ email, name, label: role ? `${role} — ${name}` : name });
  });

  const addRecipient = (email, name) => {
    const t = tagifyRef.current;
    if (!t) return;
    t.addTags([{ value: email, name: name || email }]);
    showAdvanceSendToChanged((t.value || []).map((tag) => tag.value).join(","));
  };

  const previewDaySheet = isPdf ? daySheet : { template: "" };
  const previewUrl = showAdvanceUrl(team, confirm, showAdvanceSend, previewDaySheet);
  const previewKey = [
    mode, daySheet.template, daySheet.font_size, FIELDS.map((f) => (daySheet.fields[f] ? 1 : 0)).join(""),
    selectedNoteIds.join(","), confirm.hide_lineup_from_advance, confirm.hide_show_details_from_advance,
  ].join("-");

  const sectionCount = FIELDS.filter((f) => daySheet.fields[f]).length;
  const recipients = recipientCount(showAdvanceSend);
  const summary = isEmail
    ? `${recipients} recipient${recipients === 1 ? "" : "s"}`
    : isDaySheet
      ? `${sectionCount} of ${FIELDS.length} sections · ${daySheet.font_size} type`
      : "Standard advance";

  const close = () => showAdvanceSendChanged({});

  const contentStyle = isMobile
    ? { ...modalStyle, width: "100vw", maxWidth: "100vw", height: "100dvh", borderRadius: "0" }
    : modalStyle;

  return (
  <div>
    <Popup
      open={(Object.keys(showAdvanceSend).length > 0)}
      contentStyle={contentStyle}
      closeOnDocumentClick={false}
      className={"popup-modal"}
      onOpen={() => {
        showAdvanceSendSubjectChanged(confirm.human_start_date + " - " + confirm.name + " at " + confirm.venue.name + " Show Advance");
      }}
      onClose={() => showAdvanceSendChanged({})}
    >
      <div className="sl-popup" style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>

        <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", padding: "20px 24px",
                      borderBottom: "1px solid var(--sl-border-secondary)", flex: "none" }}>
          <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: "var(--sl-bg-brand-primary)",
                        border: "1px solid var(--sl-bg-brand-secondary)", display: "flex", alignItems: "center",
                        justifyContent: "center", flex: "none" }}>
            <DocumentIcon fill="var(--sl-text-brand)" width="20" height="20" />
          </div>
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
            <div style={{ fontSize: "18px", lineHeight: "28px", fontWeight: 600, color: "var(--sl-text-primary)" }}>Show advance</div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", lineHeight: "20px",
                          color: "var(--sl-text-tertiary)", flexWrap: "wrap" }}>
              <span style={{ fontWeight: 600, color: "var(--sl-text-secondary)" }}>{confirm.name}</span>
              <span style={{ width: "3px", height: "3px", borderRadius: "99px", background: "var(--sl-fg-quinary)" }} />
              <span>{confirm.human_start_date}</span>
              <span style={{ width: "3px", height: "3px", borderRadius: "99px", background: "var(--sl-fg-quinary)" }} />
              <span>{confirm.venue.name}</span>
            </div>
          </div>
          <button type="button" className="sl-popup-close" onClick={close} aria-label="Close"
                  style={{ width: "36px", height: "36px", flex: "none", borderRadius: "8px", display: "flex",
                           alignItems: "center", justifyContent: "center", fontSize: "20px" }}>
            ×
          </button>
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: isMobile ? "column" : "row",
                      minHeight: 0, overflowY: isMobile ? "auto" : "visible" }}>

          <div style={{ width: isMobile ? "100%" : "452px", flex: "none",
                        borderRight: isMobile ? "none" : "1px solid var(--sl-border-secondary)",
                        borderBottom: isMobile ? "1px solid var(--sl-border-secondary)" : "none",
                        display: "flex", flexDirection: "column", minHeight: 0, background: "var(--sl-bg-primary)" }}>

            <div style={{ padding: "16px 24px", borderBottom: "1px solid var(--sl-border-tertiary)", flex: "none" }}>
              <div className="sl-seg-group">
                {canSend ? (
                  <button type="button" className={`sl-seg-btn${isEmail ? " is-active" : ""}`} onClick={() => setMode("email")}>
                    <span style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
                      <MailIcon width="15" height="15" /> Email it
                    </span>
                  </button>
                ) : null}
                <button type="button" className={`sl-seg-btn${isPdf ? " is-active" : ""}`} onClick={() => setMode("pdf")}>
                  <span style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
                    <DownloadIcon width="15" height="15" /> Download PDF
                  </span>
                </button>
              </div>
            </div>

            <div style={{ flex: isMobile ? "none" : 1, overflowY: isMobile ? "visible" : "auto", minHeight: 0 }}>

              <div style={{ padding: "20px 24px", display: isPdf ? "flex" : "none", flexDirection: "column", gap: "16px" }}>
                  <SectionHeading title="What's in the sheet" subtitle="Pick a layout, then choose which sections print." />

                  <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    <FieldLabel>Layout</FieldLabel>
                    <div style={{ position: "relative", display: "flex" }}>
                      <select name="day-sheet-template" value={daySheet.template}
                              onChange={(e) => selectLayout(e.target.value)}
                              style={{ appearance: "none", width: "100%", padding: "10px 40px 10px 14px", fontSize: "16px",
                                       lineHeight: "24px", color: "var(--sl-text-primary)", background: "var(--sl-bg-primary)",
                                       border: "1px solid var(--sl-border-primary)", borderRadius: "8px",
                                       boxShadow: "var(--sl-shadow-xs)", cursor: "pointer" }}>
                        {LAYOUTS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                      </select>
                      <span style={{ position: "absolute", right: "12px", top: "12px", pointerEvents: "none", display: "flex" }}>
                        <ChevronDownIcon fill="var(--sl-text-quaternary)" width="20" height="20" />
                      </span>
                    </div>
                    <span style={{ fontSize: "12px", lineHeight: "18px", color: "var(--sl-text-quaternary)" }}>{layoutHint}</span>
                  </label>

                  {isDaySheet ? (
                    <React.Fragment>
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        <FieldLabel>Sections</FieldLabel>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                          {SECTIONS.map((s) => {
                            const forced = (FORCE_OFF[daySheet.template] || []).includes(s.id);
                            return (
                              <DaySheetSectionCard key={s.id} id={s.id} label={s.label} desc={s.desc}
                                                   checked={!!daySheet.fields[s.id] && !forced} disabled={forced}
                                                   onToggle={toggleField} />
                            );
                          })}
                        </div>
                      </div>

                      {daySheet.fields.notes && !(FORCE_OFF[daySheet.template] || []).includes("notes") && notes.length > 0 ? (
                        <NotesPicker notes={notes} selectedNoteIds={selectedNoteIds} onToggle={showAdvanceSendNoteClicked} />
                      ) : null}

                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                        <FieldLabel>Text size</FieldLabel>
                        <div className="sl-seg-group sl-seg-group--compact">
                          {FONT_SIZES.map((f) => (
                            <button key={f.value} type="button"
                                    className={`sl-seg-btn${daySheet.font_size === f.value ? " is-active" : ""}`}
                                    onClick={() => setDaySheet((d) => ({ ...d, font_size: f.value }))}>{f.label}</button>
                          ))}
                        </div>
                      </div>
                    </React.Fragment>
                  ) : null}

                  <div style={{ display: "flex", gap: "12px", padding: "14px", background: "var(--sl-bg-secondary)",
                                border: "1px solid var(--sl-border-secondary)", borderRadius: "10px" }}>
                    <span style={{ flex: "none", marginTop: "1px", display: "flex" }}>
                      <DownloadIcon fill="var(--sl-text-quaternary)" width="20" height="20" />
                    </span>
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                      <div style={{ fontSize: "14px", lineHeight: "20px", fontWeight: 600, color: "var(--sl-text-secondary)" }}>Opens a printable PDF</div>
                      <div style={{ fontSize: "13px", lineHeight: "19px", color: "var(--sl-text-quaternary)" }}>One page, ready to print for the backstage wall.</div>
                    </div>
                  </div>
                </div>

              {canSend ? (
                <div style={{ padding: "20px 24px 28px", display: isEmail ? "flex" : "none", flexDirection: "column", gap: "16px" }}>
                  <SectionHeading title="Who gets it" subtitle="Sends the standard advance with the event details, lineup, and notes you choose." />

                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    <FieldLabel>To</FieldLabel>
                    <Tags
                      tagifyRef={tagifyRef}
                      placeholder={"Add an email or contact"}
                      settings={{
                        pattern: /[^@\s]+@[^@\s]+/,
                        tagTextProp: "name",
                        dropdown: { searchKeys: ["name", "value"] },
                        whitelist: eventContacts
                          .filter((cec) => cec.id)
                          .filter((cec) => cec.contact && cec.contact.email && cec.contact.email.length > 0)
                          .map((cec) => Object.assign({}, cec.contact, { value: cec.contact.email })),
                        templates: {
                          dropdownItem: function (item) {
                            return `<div ${this.getAttributes(item)}
                              class='${this.settings.classNames.dropdownItem} ${item.class ? item.class : ""}'
                              tabindex="0"
                              role="option">${item.name} - ${item.value}</div>`;
                          }
                        }
                      }}
                      onChange={(e) => {
                        showAdvanceSendToChanged((e.detail.tagify.value || []).map((tag) => tag.value).join(","));
                      }}
                    />
                    {contactSuggestions.length > 0 ? (
                      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "2px" }}>
                        {contactSuggestions.map((s) => (
                          <button key={s.email} type="button" className="sl-contact-chip"
                                  onClick={() => addRecipient(s.email, s.name)}>
                            <PlusIcon width="11" height="11" /> {s.label}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>

                  <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    <FieldLabel>Subject</FieldLabel>
                    <input type="text" className="sl-popup-input" value={showAdvanceSend.subject || ""}
                           onChange={(e) => showAdvanceSendSubjectChanged(e.target.value)} id="show-advance-send-subject" />
                  </label>

                  <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    <FieldLabel aside={<span style={{ fontWeight: 400, color: "var(--sl-text-quaternary)", fontSize: "12px" }}>Optional</span>}>Message</FieldLabel>
                    <textarea className="sl-popup-textarea" placeholder={"Anything the tour should know before load-in…"}
                              value={showAdvanceSend.body || ""} rows="4" id="show-advance-send-body"
                              onChange={(e) => showAdvanceSendBodyChanged(e.target.value)} />
                  </label>

                  {notes.length > 0 ? (
                    <NotesPicker notes={notes} selectedNoteIds={selectedNoteIds} onToggle={showAdvanceSendNoteClicked} />
                  ) : null}
                </div>
              ) : null}

            </div>
          </div>

          <div style={{ flex: isMobile ? "none" : 1, height: isMobile ? "70vh" : "auto", minWidth: 0,
                        background: "var(--sl-bg-page)", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px",
                          borderBottom: "1px solid var(--sl-border-secondary)", background: "var(--sl-bg-secondary)", flex: "none" }}>
              <span style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", lineHeight: "18px",
                             fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", color: "var(--sl-text-quaternary)" }}>
                <EyeIcon width="14" height="14" />
                Preview
              </span>
              <span style={{ fontSize: "12px", lineHeight: "18px", color: "var(--sl-text-quaternary)" }}>
                {isPdf ? (isDaySheet ? "Day sheet" : "Standard advance") : "Advance email"}
              </span>
            </div>
            <div style={{ flex: 1, minHeight: 0, display: "flex", background: "#FFFFFF" }}>
              <Iframe key={previewKey} url={previewUrl} width="100%" height="100%"
                      styles={{ border: "none", flex: 1, display: "block" }} className="sl-popup-preview-iframe" />
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px",
                      padding: "16px 24px", borderTop: "1px solid var(--sl-border-secondary)", background: "var(--sl-bg-primary)", flex: "none" }}>
          <div style={{ fontSize: "13px", lineHeight: "19px", color: "var(--sl-text-quaternary)" }}>{summary}</div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <button type="button" className="btn-setlist-tertiary" onClick={close}>Cancel</button>
            {isPdf ? (
              <button type="button" className="btn-setlist-primary"
                      onClick={() => { window.open(showAdvanceUrl(team, confirm, showAdvanceSend, daySheet, ".pdf"), "_blank"); close(); }}>
                Download PDF
              </button>
            ) : (
              <input type="submit" className="btn-setlist-primary" value="Send advance"
                     disabled={!canSubmitForm(showAdvanceSend)}
                     onClick={(e) => { e.preventDefault(); if (canSubmitForm(showAdvanceSend)) createShowAdvanceSend(csrfToken, team, showAdvanceSend); }} />
            )}
          </div>
        </div>
      </div>
    </Popup>
  </div>
  );
};

ShowAdvanceSendPopup.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  showAdvanceSend: PropTypes.object,
  showAdvanceSendChanged: PropTypes.func.isRequired,
  showAdvanceSendToChanged: PropTypes.func.isRequired,
  showAdvanceSendSubjectChanged: PropTypes.func.isRequired,
  showAdvanceSendBodyChanged: PropTypes.func.isRequired,
  createShowAdvanceSend: PropTypes.func.isRequired,
  calendarEventContacts: PropTypes.array,
  notes: PropTypes.array,
  showAdvanceSendNoteClicked: PropTypes.func.isRequired,
  toggleHideLineupFromAdvance: PropTypes.func.isRequired,
  toggleHideShowDetailsFromAdvance: PropTypes.func.isRequired,
  initialMode: PropTypes.oneOf(["email", "pdf"])
};

ShowAdvanceSendPopup.defaultProps = {
  calendarEventContacts: []
};

export default ShowAdvanceSendPopup;
